import React, { useState } from 'react';
import type { TestResult } from '../types/worker';

interface ResultsPanelProps {
  results?: TestResult[];
  stdout?: string;
  stderr?: string;
  error?: string;
  mode?: 'idle' | 'code' | 'tests';
}

export const ResultsPanel: React.FC<ResultsPanelProps> = ({
  results = [],
  stdout = '',
  stderr = '',
  error = '',
  mode = 'idle'
}) => {
  const [expandedTests, setExpandedTests] = useState<Set<number>>(new Set());

  const toggleTest = (index: number) => {
    const newExpanded = new Set(expandedTests);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTests(newExpanded);
  };

  if (mode === 'idle') {
    return (
      <div className="panel results-panel">
        <h3>Results</h3>
        <div style={{ fontFamily: 'monospace', fontSize: '0.9em', color: 'var(--muted-foreground)' }}>
          <p>Run your code to see results...</p>
        </div>
      </div>
    );
  }

  if (mode === 'code') {
    return (
      <div className="panel results-panel">
        <h3>Output</h3>
        <div style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
          {error && (
            <div style={{ color: 'var(--destructive)', marginBottom: '1rem' }}>
              <strong>Error:</strong>
              <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{error}</pre>
            </div>
          )}
          {stdout && (
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: 'var(--success)' }}>stdout:</strong>
              <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem', color: 'var(--muted-foreground)' }}>
                {stdout}
              </pre>
            </div>
          )}
          {stderr && (
            <div>
              <strong style={{ color: 'var(--warning)' }}>stderr:</strong>
              <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem', color: 'var(--muted-foreground)' }}>
                {stderr}
              </pre>
            </div>
          )}
          {!error && !stdout && !stderr && (
            <p style={{ color: 'var(--muted-foreground)' }}>No output</p>
          )}
        </div>
      </div>
    );
  }

  // mode === 'tests'
  const passedCount = results.filter((r) => r.verdict === 'passed').length;
  const totalCount = results.length;
  const allPassed = passedCount === totalCount && totalCount > 0;

  return (
    <div className="panel results-panel">
      <h3>Test Results</h3>

      {error && (
        <div style={{ color: 'var(--destructive)', marginBottom: '1rem', fontFamily: 'monospace' }}>
          <strong>Error:</strong>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{error}</pre>
        </div>
      )}

      {!error && results.length > 0 && (
        <>
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              // Using slightly transparent versions or closest equivalents if full opacity vars are needed
              // Ideally, we'd use Tailwind's bg-opacity utilities if this was using classes, 
              // but since we are using style prop with vars, we might need a workaround or just use the base color.
              // For now, let's use the semantic vars directly or a subtle background if available.
              // Since we don't have bg-success-subtle in the simple variable list, we will rely on 
              // the user's preference or just use the variable. 
              // To maintain the "darker background" look, we might need new variables or just use 'var(--card)' with a border.
              backgroundColor: allPassed ? 'var(--success)' : 'var(--destructive)', 
              borderRadius: '4px',
              fontWeight: 'bold',
              color: allPassed ? 'var(--background)' : 'var(--foreground)', // Contrast check
              opacity: 0.9
            }}
          >
            {passedCount} / {totalCount} tests passed
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {results.map((result, index) => (
              <div
                key={index}
                style={{
                  border: `1px solid ${result.verdict === 'passed' ? 'var(--success)' : 'var(--destructive)'}`,
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}
              >
                <div
                  onClick={() => toggleTest(index)}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: result.verdict === 'passed' ? 'rgba(var(--success), 0.1)' : 'rgba(var(--destructive), 0.1)', // Fallback if not using full tailwind
                    // Since vars are oklch, rgba won't work directly on them. 
                    // We'll stick to 'var(--muted)' for background to be safe and use border for status.
                    // Or actually, let's just use the card background.
                    background: 'var(--muted)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span
                      style={{
                        color: result.verdict === 'passed' ? 'var(--success)' : 'var(--destructive)',
                        fontWeight: 'bold'
                      }}
                    >
                      {result.verdict === 'passed' ? '✓' : '✗'}
                    </span>
                    <span style={{ fontFamily: 'monospace' }}>{result.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>
                      {result.runtime.toFixed(1)}ms
                    </span>
                    <span style={{ fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>
                      {expandedTests.has(index) ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {expandedTests.has(index) && (
                  <div
                    style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--card)',
                      borderTop: `1px solid ${result.verdict === 'passed' ? 'var(--success)' : 'var(--destructive)'}`,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }}
                  >
                    {result.error ? (
                      <div style={{ color: 'var(--destructive)' }}>
                        <strong>Error:</strong>
                        <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>
                          {result.error}
                        </pre>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: 'var(--muted-foreground)' }}>Input:</strong>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              marginTop: '0.25rem',
                              color: 'var(--foreground)'
                            }}
                          >
                            {JSON.stringify(result.args, null, 2)}
                          </pre>
                        </div>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: 'var(--muted-foreground)' }}>Expected:</strong>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              marginTop: '0.25rem',
                              color: 'var(--foreground)'
                            }}
                          >
                            {JSON.stringify(result.expected, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <strong style={{ color: 'var(--muted-foreground)' }}>Actual:</strong>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              marginTop: '0.25rem',
                              color: result.verdict === 'passed' ? 'var(--success)' : 'var(--destructive)'
                            }}
                          >
                            {JSON.stringify(result.actual, null, 2)}
                          </pre>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {(stdout || stderr) && (
        <div style={{ marginTop: '1rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {stdout && (
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: 'var(--success)' }}>stdout:</strong>
              <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.25rem', color: 'var(--muted-foreground)' }}>
                {stdout}
              </pre>
            </div>
          )}
          {stderr && (
            <div>
              <strong style={{ color: 'var(--warning)' }}>stderr:</strong>
              <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.25rem', color: 'var(--muted-foreground)' }}>
                {stderr}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
